import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

function ensureAbsoluteUrl(siteUrl, maybeRelativeUrl) {
  if (!maybeRelativeUrl) return null;
  if (maybeRelativeUrl.startsWith('http://') || maybeRelativeUrl.startsWith('https://')) {
    return maybeRelativeUrl;
  }
  const base = (siteUrl || '').replace(/\/+$/, '');
  const path = maybeRelativeUrl.startsWith('/') ? maybeRelativeUrl : `/${maybeRelativeUrl}`;
  return `${base}${path}`;
}

function stripHtml(text) {
  if (!text) return '';
  return String(text).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

function clamp(text, max = 160) {
  const t = stripHtml(text);
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).trimEnd()}…`;
}

const DEFAULTS = {
  siteName: 'Whales Advisory',
  titleTemplate: '%s | Whales Advisory',
  defaultTitle: 'Whales Advisory | Clear Advice. Practical Solutions.',
  defaultDescription:
    'Whales Advisory provides strategic legal and corporate advisory services, supporting clients across transactions, regulation, and complex commercial matters.',
  locale: 'en_US',
  twitterCard: 'summary_large_image',
  brandImagePath: '/logo.webp',
};

/**
 * SEO meta tags per page (React Router v6).
 *
 * Configure your production site URL via env:
 * - REACT_APP_SITE_URL=https://your-domain.com
 */
export default function SEO({
  title,
  description,
  image,
  canonicalPath,
  lang = 'en',
  dir,
  noIndex = false,
  type = 'website',
}) {
  const location = useLocation();
  const siteUrl = process.env.REACT_APP_SITE_URL || '';

  const resolvedTitle = title || DEFAULTS.defaultTitle;
  const resolvedDescription = clamp(description || DEFAULTS.defaultDescription, 160);

  const path = canonicalPath ?? location.pathname;
  const canonicalUrl = siteUrl ? ensureAbsoluteUrl(siteUrl, path) : null;

  const img = image || DEFAULTS.brandImagePath;
  const imageUrl = siteUrl ? ensureAbsoluteUrl(siteUrl, img) : null;

  const robots = noIndex ? 'noindex, nofollow' : 'index, follow';

  return (
    <Helmet>
      <html lang={lang} {...(dir ? { dir } : {})} />
      <title>
        {title ? DEFAULTS.titleTemplate.replace('%s', title) : resolvedTitle}
      </title>

      <meta name="description" content={resolvedDescription} />
      <meta name="robots" content={robots} />

      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      <meta property="og:site_name" content={DEFAULTS.siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title || DEFAULTS.defaultTitle} />
      <meta property="og:description" content={resolvedDescription} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      <meta property="og:locale" content={DEFAULTS.locale} />

      <meta name="twitter:card" content={DEFAULTS.twitterCard} />
      <meta name="twitter:title" content={title || DEFAULTS.defaultTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
    </Helmet>
  );
}

